// Pure pagination helpers shared by the Comparison and Instructions pages.
// Kept framework-free so the paging logic is unit-testable without a DOM.

/** A page number, or a non-interactive gap in the page list. */
export type PaginationItem = number | 'ellipsis';

/** Render every page inline while the total stays at or below this count. */
export const MAX_INLINE_PAGES = 7;

const toPositiveInt = (value: number, fallback: number): number => {
  const normalized = Math.floor(value);
  return Number.isFinite(normalized) && normalized > 0 ? normalized : fallback;
};

const clamp = (value: number, min: number, max: number): number =>
  Math.min(Math.max(value, min), max);

const range = (from: number, to: number): number[] =>
  Array.from({ length: Math.max(0, to - from + 1) }, (_, index) => from + index);

/** Total page count for a list length; always at least 1 so the UI is stable. */
export const getTotalPages = (itemCount: number, pageSize: number): number => {
  const size = toPositiveInt(pageSize, 1);
  const count = Math.max(0, Math.floor(itemCount) || 0);
  return Math.max(1, Math.ceil(count / size));
};

/** Returns the slice of `items` visible on `page` (1-based). */
export const slicePage = <T>(
  items: readonly T[],
  page: number,
  pageSize: number,
): T[] => {
  const size = toPositiveInt(pageSize, 1);
  const totalPages = getTotalPages(items.length, size);
  const safePage = clamp(toPositiveInt(page, 1), 1, totalPages);
  const start = (safePage - 1) * size;
  return items.slice(start, start + size);
};

/**
 * Builds the page list, collapsing distant pages into ellipses.
 *
 * Short lists render every page. Long lists render the first page, the current
 * page with one neighbour on each side, the last page, and ellipses for the
 * gaps, e.g. `1 … 5 6 7 … 9`.
 */
export const buildPaginationItems = (
  currentPage: number,
  totalPages: number,
): PaginationItem[] => {
  const total = toPositiveInt(totalPages, 1);
  const page = clamp(toPositiveInt(currentPage, 1), 1, total);

  if (total <= MAX_INLINE_PAGES) {
    return range(1, total);
  }

  const items: PaginationItem[] = [1];
  const windowStart = Math.max(2, page - 1);
  const windowEnd = Math.min(total - 1, page + 1);

  if (windowStart > 2) {
    items.push('ellipsis');
  }
  items.push(...range(windowStart, windowEnd));
  if (windowEnd < total - 1) {
    items.push('ellipsis');
  }
  items.push(total);

  return items;
};
